import { Input, Menu, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import './styles.less';
import { DownOutlined, SearchOutlined, MoreOutlined, LoadingOutlined } from '@ant-design/icons';
import { history, RoutePaths } from 'core/history';
import { EventBus } from 'services/EventBus';
import { find, isEmpty } from 'lodash';
import NoUserFoundImage from 'assets/images/no_user_found.svg';
import EditIcon from 'assets/images/edit-icon.svg';
import NewOrgIcon from 'assets/images/new-org-icon.svg';
import ManageDivisionsIcon from 'assets/icons/manage-divisions.svg';
import CheckIcon from 'assets/icons/check.svg';
import { removeClassNameFromID } from 'core/common-methods';
import { useTranslation } from 'react-i18next';
import { translationWrapper } from 'utils/common-methods';
import { INavigationRouteProps } from 'core/navigation/models';
import { OrganizationDTO } from 'core/orgs/models';
import { createNewOrganizationDeepAction } from 'services/DeepActions';
import { useSelector } from 'react-redux';
import { getIsHierarchyManagementEnabled } from 'app-redux/selectors/rollout.selector';
import { Popover } from 'syngenta-digital-cropwise-react-ui-kit';
import { RBAC_ACTIONS } from 'services/rbacActions';
import { verifyRBACPermission } from 'services/rbac';
import { AllowedAndDeniedOperationsDTO, OperationsDTO } from 'core/rbac/models';
import { IWorkspace } from 'core/workspaces/models';

const WORKSPACE_NAME_HIGHLIGHT_TIME = 5000;

interface IWorkspaceSelection extends INavigationRouteProps<{}> {
  workspaces: any,
  setCurrentWorkspaceId: any,
  setCurrentOrg: any,
  currentWorkspaceId: string,
  orgs: OrganizationDTO[],
  currentOrg: OrganizationDTO;
}

interface IWorkspaceSubmenu {
  workspaceId: string;
  workspaces: IWorkspace[];
  setPopoverVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setWorkspaceSubmenuPopoverVisible: React.Dispatch<React.SetStateAction<boolean>>;
  t: any;
  currentWorkspaceId: string;
}

const WorkspaceSubmenu = ({ workspaceId, workspaces, setPopoverVisible, setWorkspaceSubmenuPopoverVisible, t, currentWorkspaceId }: IWorkspaceSubmenu) => {
  const [hasLoadedPermissions, setHasLoadedPermissions] = useState<boolean>(false);
  const [canUserAccessHierarchyMgt, setCanUserAccessHierarchyMgt] = useState<boolean>(false);
  const isHierarchyManagementEnabled = useSelector(getIsHierarchyManagementEnabled);

  const editWorkSpace = () => {
    setPopoverVisible(false);
    setWorkspaceSubmenuPopoverVisible(false);
    EventBus.$emit('workspaceEditEvent', find(workspaces, { id: workspaceId }));
  }

  useEffect(() => {
    const checkHierarchyAccessFromUser = async () => {
      const rbacOperations =
      {
        operations: [
          {
            action: RBAC_ACTIONS.role.write,
            resource: `crn:workspace:${workspaceId}`,
          }
        ]
      };
  
      const { data } = await verifyRBACPermission(rbacOperations);
      const allowedOperations = (data as AllowedAndDeniedOperationsDTO).allowed_operations;
      if (!isEmpty(allowedOperations)) {
        setCanUserAccessHierarchyMgt(!!allowedOperations.find((allowedOperation: OperationsDTO) => allowedOperation.action === RBAC_ACTIONS.role.write))
      }
      setHasLoadedPermissions(true);
    }
    if (workspaceId) {
      checkHierarchyAccessFromUser();
    }
  }, [workspaceId])

  return (
    <div className='workspace-submenu-container'>
      {
        hasLoadedPermissions ? 
          <Menu className='edit-workspace-menu-container' selectedKeys={[]}>
            <Menu.Item key="edit-workspace" onClick={editWorkSpace}>
              <>
                <img className="edit-workspace-icon-container" src={EditIcon} alt='edit-icon'></img>
                <span>{t('global.side_bar.title.edit_workspace')}</span>
              </>
            </Menu.Item>
            <Menu.Item key="new-organization"
              onClick={() => createNewOrganizationDeepAction(workspaceId)}>
              <div>
                <img className="edit-workspace-icon-container" src={NewOrgIcon} alt='new-org-icon'></img>
                <span>{t('global.side_bar.title.new_organization')}</span>
              </div>
            </Menu.Item>
            {
              isHierarchyManagementEnabled &&
              <Menu.Item
                key="manage-divisions"
                disabled={!canUserAccessHierarchyMgt}
                onClick={() => history.push({
                  pathname: RoutePaths.HIERARCHY_MANAGEMENT(),
                  search: `?workspace=${workspaceId}`,
                })}
              >
                <div>
                  <img className="edit-workspace-icon-container" src={ManageDivisionsIcon} alt='manage-divisions-con'></img>
                  <span>{t('global.side_bar.title.manage_divisions')}</span>
                </div>
              </Menu.Item>
            }
          </Menu>
            :
          <LoadingOutlined/>
      }
    </div>
  );
}

const WorkspaceSubmenuPopover = (props: any) => {
  const [workspaceSubmenuPopoverVisible, setWorkspaceSubmenuPopoverVisible] = useState<boolean>(false);
  const workspaceSubmenuComponent = (
    <WorkspaceSubmenu
      workspaceId={props.workspace}
      workspaces={props.workspaces}
      setPopoverVisible={props.setPopoverVisible}
      setWorkspaceSubmenuPopoverVisible={setWorkspaceSubmenuPopoverVisible}
      t={props.t}
      currentWorkspaceId={props.currentWorkspaceId}
    />
  )
  return (
    <Popover
      placement="bottomLeft"
      trigger="click"
      onVisibleChange={(visible: boolean) => {
        setWorkspaceSubmenuPopoverVisible(visible);
      }}
      visible={workspaceSubmenuPopoverVisible}
      overlayClassName='workspace-submenu-dropdown'
      overlay={workspaceSubmenuComponent}
      content={workspaceSubmenuComponent}
    >
      <div className='more-icon-container'>
        <MoreOutlined />
      </div>
    </Popover>
  )
}

export const WorkspaceSelection: React.FC<IWorkspaceSelection> = (props) => {
  const { t } = translationWrapper(useTranslation());
  const { workspaces = [], setCurrentWorkspaceId = () => false, setCurrentOrg = () => false, orgs = [] } = props;
  const [selectedOrganizationName, setSelectedOrganizationName] = useState<string>();
  const [searchOrganization, setSearchOrganization] = useState('');
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const currentOrgId = props.currentOrg?.id;

  const setCurrentWSAndOrg = (orgId: string, workspaceId?: string) => {
    setCurrentOrg(orgId);
    setCurrentWorkspaceId(workspaceId);
    setSelectedOrganizationName(orgs.find((org: OrganizationDTO) => org.id === orgId)?.name);
  }

  const onOrganizationSelection = (org: OrganizationDTO) => {
    setCurrentWSAndOrg(org.id, org.workspace_id);
    setPopoverVisible(false);
    history.push({ pathname: RoutePaths.OVERVIEW(), search: `?workspace=${org.workspace_id}&organization=${org.id}` });
  };

  const fetchCurrentData = () => {
    const workspaceFromURL = new URLSearchParams(props.location.search).get('workspace');
    const orgIdFromURL = new URLSearchParams(props.location.search).get('organization');
    const firstOrgFromWorkspace = orgs?.find((org) => org.workspace_id === workspaceFromURL)?.id;

    // We can have workspace and org on the url
    if(workspaceFromURL && orgIdFromURL) {
      const isOrgFromWS = orgs?.find(org => org.id === orgIdFromURL && org.workspace_id === workspaceFromURL)
      
      // If valid set both, if not get the first org from the ws
      if(isOrgFromWS) {
        setCurrentWSAndOrg(orgIdFromURL, workspaceFromURL);
        return
      } else if(firstOrgFromWorkspace) {
        setCurrentWSAndOrg(firstOrgFromWorkspace, workspaceFromURL);
        return  
      }
    }

    // Or only org
    if(orgIdFromURL) {
      const selectedOrgWorkspace = orgs?.find((org: OrganizationDTO) => org.id === orgIdFromURL)?.workspace_id;
      if(selectedOrgWorkspace) {
        setCurrentWSAndOrg(orgIdFromURL, selectedOrgWorkspace);
        return
      }
    }

    // Or only ws
    if(workspaceFromURL) {
      if (firstOrgFromWorkspace) {
        setCurrentWSAndOrg(firstOrgFromWorkspace, workspaceFromURL);
        return
      }
    }

    // If none is set, we use the first org
    const firstOrg = orgs[0].id;
    const selectedOrgWorkspace = orgs?.find((org: OrganizationDTO) => org.id === firstOrg)?.workspace_id;
    setCurrentWSAndOrg(firstOrg, selectedOrgWorkspace)
  }

  useEffect(() => {
    if (orgs?.length) {
      fetchCurrentData();
      setTimeout(() => {
        removeClassNameFromID("workspace-selection-container", "workspace-selection-container-page-landed")
      }, WORKSPACE_NAME_HIGHLIGHT_TIME)
    }
  }, [orgs]);

  const getOrgsListByWorkspace = (workspaceId: string) => { 
    return orgs
      .filter((org: OrganizationDTO) => org.workspace_id === workspaceId)
      .map((org: OrganizationDTO) => {
      let filteredMenu: any = (
        <Menu.Item
          selectedKeys={[currentOrgId]}
          onClick={() => onOrganizationSelection(org)}
          key={org.id}
        >
          <div style={{ display: 'flex' }}>
            <div className='check-icon-container'>
              {currentOrgId === org.id ? <img src={CheckIcon}></img> : null}
            </div>
            <div className='workspace-name'>{org.name}</div>
          </div>
        </Menu.Item>
      );
      searchOrganization && !org.name?.toLowerCase().includes(searchOrganization.toLowerCase()) && (filteredMenu = null);
      return filteredMenu;
    })
  };

  const filteredOrgsByWorkspaces = workspaces.map((workspace: any) => {
    const orgsListByWorkspace = getOrgsListByWorkspace(workspace.id).filter((orgList: any) => !!orgList);
    if (orgsListByWorkspace.length > 0) {
      return (
        <Menu.ItemGroup
          key={workspace.id}
          title={
            <>
              {workspace.name}
              <WorkspaceSubmenuPopover
                workspace={workspace.id}
                workspaces={workspaces}
                setPopoverVisible={setPopoverVisible}
                currentWorkspaceId={props.currentWorkspaceId}
                t={t}
              />
            </>
          }
        >
          {orgsListByWorkspace}
        </Menu.ItemGroup>
      )
    }
    return <></>
  }) || [];

  const menu = () => (
    <div>
      {
        filteredOrgsByWorkspaces.length == 0 ?
        <div className='no-workspace-found-container'>
          <img src={NoUserFoundImage} alt='no data found'></img>
          <p>{t('global.side_bar.info.no_workspaces_found')}</p>
        </div>
        :
        <>
          <Menu
            selectable
            className='workspace-list-menu'
            selectedKeys={[currentOrgId]}
          >
            {filteredOrgsByWorkspaces}
          </Menu>
        </>
      }
    </div>
  );

  return (
    <div id='workspace-selection-container' className={`workspace-selection-container workspace-selection-container-page-landed`}>
      <Popover
        overlayStyle={{ width: '320px', height: '249px' }}
        placement="bottomLeft"
        trigger="click"
        onVisibleChange={(newVisible: boolean) => {
          setPopoverVisible(newVisible);
        }}
        visible={popoverVisible}
        title={<div>
          <span className='workspace-title'>{t('global.side_bar.title.organization')}</span>
          {
            orgs.length > 1 ?
            <div className='workspace-search-box-container'>
              <Input
                onClick={(e) => {
                  e.stopPropagation()
                }}
                prefix={<SearchOutlined />}
                onChange={(e) => { setSearchOrganization(e.target.value) }}
                className='workspace-search-box'
                placeholder={t("global.side_bar.place_holder.search_an_organization")}
              />
            </div>
            :
            <></>
          }
        </div>
        }
        overlayClassName='workspace-selection-dropdown' overlay={menu} content={menu}
      >
        <div className={`selections-box ${popoverVisible ? "hover-on-selection" :'hover-on-box'}`}>
          <div>
            <span className='workspace-title'>{t('global.side_bar.title.organization')}</span>
            <Space>
              <div className='selected-workspace-name'>
                {selectedOrganizationName ? selectedOrganizationName : t('global.info.loading')}
              </div>
              <DownOutlined />
            </Space>
          </div>
        </div>
      </Popover>
    </div>
  );
};